/**
 * URL Validators
 * Domain-specific URL validation for social media platforms
 */

import { z } from "zod"

/**
 * Creates a domain-specific URL validator using Zod
 * @param allowedDomains - Array of allowed domain names (e.g., ['twitter.com', 'x.com'])
 * @returns A Zod schema that validates the URL hostname against allowed domains
 */
export const createSocialUrlValidator = (allowedDomains: string[]) => {
  return z
    .string()
    .transform(val => val.trim())
    .transform(val => {
      // If doesn't start with http/https, add it
      if (val && !/^https?:\/\//i.test(val)) {
        return `https://${val}`
      }
      return val
    })
    .refine(
      val => {
        if (!val || val === "") return true
        try {
          const url = new URL(val)
          const hostname = url.hostname.toLowerCase()
          return allowedDomains.some(
            domain => hostname === domain || hostname.endsWith(`.${domain}`),
          )
        } catch {
          return false
        }
      },
      {
        message: `URL must be from ${allowedDomains.join(" or ")}`,
      },
    )
    .optional()
    .or(z.literal(""))
}

/**
 * Generic website URL validator (any domain allowed)
 */
export const websiteUrlValidator = z
  .string()
  .transform(val => val.trim())
  .transform(val => {
    // If doesn't start with http/https, add it
    if (val && !/^https?:\/\//i.test(val)) {
      return `https://${val}`
    }
    return val
  })
  .refine(
    val => {
      if (!val || val === "") return true
      try {
        const url = new URL(val)
        return url.protocol === "http:" || url.protocol === "https:"
      } catch {
        return false
      }
    },
    { message: "Please enter a valid URL" },
  )
  .optional()
  .or(z.literal(""))

/**
 * Twitter/X URL validator
 * Accepts both twitter.com and x.com domains
 */
export const twitterUrlValidator = createSocialUrlValidator(["twitter.com", "x.com"])

/**
 * Instagram URL validator
 * Accepts both full URLs and username-only input
 * - Full URL: instagram.com/username, www.instagram.com/username, https://instagram.com/username
 * - Username only: username (transforms to https://instagram.com/username)
 */
export const instagramUrlValidator = z
  .string()
  .transform(val => val.trim())
  .transform(val => {
    // If empty, return as-is
    if (!val) return val

    // Check if input is username-only (no dots, no slashes - e.g., "username")
    const isUsernameOnly = !val.includes(".") && !val.includes("/")

    if (isUsernameOnly) {
      // It's a username - transform to full URL
      return `https://instagram.com/${val}`
    }

    // It's a URL - add protocol if missing
    if (!/^https?:\/\//i.test(val)) {
      return `https://${val}`
    }
    return val
  })
  .refine(
    val => {
      if (!val || val === "") return true
      try {
        const url = new URL(val)
        const hostname = url.hostname.toLowerCase()
        return hostname === "instagram.com" || hostname.endsWith(".instagram.com")
      } catch {
        return false
      }
    },
    {
      message:
        "Instagram must be a valid username or URL (e.g., username or instagram.com/username)",
    },
  )
  .optional()
  .or(z.literal(""))

/**
 * Facebook URL validator
 * Accepts both facebook.com and fb.com domains
 */
export const facebookUrlValidator = createSocialUrlValidator(["facebook.com", "fb.com"])
